import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { ArrowSquareOutIcon, CaretRightIcon } from '@phosphor-icons/react';
import { Button } from './Button.js';
import { cx } from './cx.js';
import { FieldLabel } from './FieldLabel.js';
import { Scrim } from './Scrim.js';
import { useFocusTrap } from './useFocusTrap.js';

/**
 * A chip that opens a short list of things to do.
 *
 * Graduated from the Subscription sketch's Manage menu (section E of
 * `lobby-subscription-settings`): Change plan, Change payment, Cancel
 * subscription. A few choices, and the menu closes on the first one.
 *
 * **Two layouts, one list.** A phone gets a `sheet` that rises from the bottom
 * over a scrim, where the thumb is. A wider screen gets a `dropdown`: a panel
 * dropped from the chip's right edge. The screen chooses, because only the
 * screen knows which it is; a sketch frame is not a viewport, so a media query
 * here would get every phone frame wrong.
 *
 * **Not `Sheet`.** `Sheet` is the side drawer for reading over a live table.
 * This holds a handful of choices and is gone after one. **Not `Select`.**
 * `Select` answers "which value", and its rows carry a chosen state. These rows
 * are doors: one stays in the app and shows a caret, one leaves it and shows an
 * out arrow and a line naming where it goes.
 *
 * **It is a `dialog`, not an APG `menu`.** The rows are ordinary buttons in tab
 * order, which is all a list this short needs. The sheet is modal: it traps
 * focus, closes on Escape, the scrim, or its Close chip. The dropdown is not:
 * it closes on Escape, the chip, or a press anywhere else. Either way focus
 * returns to the chip.
 *
 * The sheet is `fixed`, so it covers whatever establishes the containing
 * block: the viewport in the app, a `contain-paint` frame in the workshop. The
 * dropdown is `absolute` under the chip, so a scrolling ancestor can clip it
 * near its bottom edge. `Select` solved that with a fixed portal; this has not
 * needed it yet, and a portal would escape every sketch frame.
 */

export interface ActionMenuItem<T extends string> {
  readonly value: T;
  /** Already translated. */
  readonly label: string;
  /** One quiet line under the label. For a row that leaves, say where it goes. */
  readonly hint?: string | undefined;
  /**
   * The row leaves the app: a store's billing page, a new tab. It gets an out
   * arrow instead of the caret, so the door says what it does before it opens.
   */
  readonly leaves?: boolean | undefined;
}

export type ActionMenuLayout = 'dropdown' | 'sheet';

export interface ActionMenuProps<T extends string> {
  /** What the chip says. Already translated. */
  readonly label: string;
  /** The menu's name: the sheet's heading, and the accessible name of both. */
  readonly title: string;
  /** The sheet's way out. Unused by the dropdown, which closes on the chip. */
  readonly closeLabel: string;
  readonly items: readonly ActionMenuItem<T>[];
  /**
   * A row was pressed. The menu has already closed; the chip comes with it so
   * a screen that navigates away can land focus back on it when it returns.
   */
  readonly onPick: (value: T, chip: HTMLButtonElement) => void;
  readonly layout?: ActionMenuLayout;
  /** Start open. For the workshop, which shows the menu without a press. */
  readonly defaultOpen?: boolean;
  /** Layout only, on the wrapper. The chip and panel are not yours. */
  readonly className?: string | undefined;
}

/* White throughout, rows ruled off in `gray-300` the way `Section` rules its
   rows. The panels carry no side padding, so the rules and the hover wash run
   to the panel's edge; the rows carry the inset. */
const ROW = 'flex min-h-14 w-full items-center gap-3 px-5 py-3 text-left hover:bg-ink/5';

const DROPDOWN =
  'absolute top-full right-0 z-10 mt-2 w-72 overflow-hidden rounded-3xl border-2 border-ink bg-white shadow-md shadow-ink';

const SHEET = 'relative rounded-t-3xl border-t-2 border-ink bg-white pt-3 pb-6 focus:outline-none';

function Rows<T extends string>({
  items,
  onPick,
  hintSize,
}: {
  readonly items: readonly ActionMenuItem<T>[];
  readonly onPick: (value: T) => void;
  readonly hintSize?: 'sm' | undefined;
}): React.JSX.Element {
  return (
    <ul className="flex list-none flex-col divide-y divide-gray-300 p-0">
      {items.map((item) => (
        <li key={item.value}>
          <button
            type="button"
            className={ROW}
            onClick={() => {
              onPick(item.value);
            }}
          >
            <FieldLabel hint={item.hint} hintSize={hintSize} className="flex-1">
              {item.label}
            </FieldLabel>
            {item.leaves === true ? (
              <ArrowSquareOutIcon size={18} className="shrink-0 text-muted" aria-hidden="true" />
            ) : (
              <CaretRightIcon size={18} className="shrink-0" aria-hidden="true" />
            )}
          </button>
        </li>
      ))}
    </ul>
  );
}

function MenuSheet({
  title,
  closeLabel,
  onClose,
  children,
}: {
  readonly title: string;
  readonly closeLabel: string;
  readonly onClose: () => void;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const titleId = useId();
  const trap = useFocusTrap<HTMLDivElement>(onClose);
  return (
    <div className="fixed inset-0 z-60 flex flex-col justify-end" onKeyDown={trap.onKeyDown}>
      <Scrim onClick={onClose} />
      <div
        ref={trap.ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={SHEET}
      >
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-ink/25" aria-hidden="true" />
        <h2
          id={titleId}
          className="px-5 pb-2 text-xs font-extrabold tracking-widest text-muted uppercase"
        >
          {title}
        </h2>
        {children}
        <div className="px-4 pt-3">
          <Button className="w-full" onClick={onClose}>
            {closeLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function ActionMenu<T extends string>({
  label,
  title,
  closeLabel,
  items,
  onPick,
  layout = 'dropdown',
  defaultOpen = false,
  className,
}: ActionMenuProps<T>): React.JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  const chip = useRef<HTMLButtonElement>(null);
  const wrapper = useRef<HTMLDivElement>(null);

  const close = (): void => {
    setOpen(false);
    chip.current?.focus();
  };

  const pick = (value: T): void => {
    close();
    if (chip.current !== null) onPick(value, chip.current);
  };

  /* A press anywhere else puts the dropdown away, as `Select`'s panel does.
     The sheet has its own scrim for that. */
  useEffect(() => {
    if (!open || layout !== 'dropdown') return undefined;
    const away = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (wrapper.current?.contains(target) === true) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', away, true);
    return () => {
      document.removeEventListener('pointerdown', away, true);
    };
  }, [open, layout]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.defaultPrevented) return;
    if (event.key === 'Escape' && open && layout === 'dropdown') close();
  };

  return (
    <div ref={wrapper} className={cx('relative', className)} onKeyDown={onKeyDown}>
      <Button
        ref={chip}
        size="sm"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => {
          setOpen((was) => !was);
        }}
      >
        {label}
      </Button>
      {open && layout === 'dropdown' && (
        <div role="dialog" aria-label={title} className={DROPDOWN}>
          {/* The dropdown is w-72; a store's name fits its hint on one line at text-sm. */}
          <Rows items={items} onPick={pick} hintSize="sm" />
        </div>
      )}
      {open && layout === 'sheet' && (
        <MenuSheet title={title} closeLabel={closeLabel} onClose={close}>
          <Rows items={items} onPick={pick} />
        </MenuSheet>
      )}
    </div>
  );
}
